import type { DocumentReference } from '@medplum/fhirtypes';
import { useId } from 'react';
import { useTranslation } from 'react-i18next';
import { EncounterDocumentsSection } from './EncounterDocumentsSection';

export type ProcedureReportDocumentsProps = {
  /** Resolved `DocumentReference` entries from `Procedure.report[]`. */
  documents: DocumentReference[] | undefined;
  isLoading?: boolean;
  /** One or more document fetches failed — renders an inline error in place of the tabs. */
  hasError?: boolean;
};

/**
 * The documents half of a procedure's `report[]`, rendered with the same tabbed section the
 * encounter detail uses (its own heading suppressed in favour of one naming the procedure).
 *
 * Renders nothing when there are no documents, but holds its place while fetches are in flight and
 * when they fail: a block that vanishes on failure is indistinguishable from a procedure that links
 * no documents at all, which is the "partial state reads as complete" trap the results table
 * already avoids.
 */
export const ProcedureReportDocuments = ({
  documents,
  isLoading = false,
  hasError = false,
}: ProcedureReportDocumentsProps): React.ReactElement | null => {
  const { t } = useTranslation('records');
  const headingId = useId();

  if (!isLoading && !hasError && (documents?.length ?? 0) === 0) return null;

  return (
    <section aria-labelledby={headingId} className="space-y-3">
      <h2 id={headingId} className="text-lg font-semibold">
        {t('procedureReports')}
      </h2>
      {hasError ? (
        <p role="alert" className="text-sm text-destructive" data-testid="procedure-documents-error">
          {t('procedureReportsError')}
        </p>
      ) : (
        <EncounterDocumentsSection documents={documents} isLoading={isLoading} showHeading={false} />
      )}
    </section>
  );
};
